import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PageHeader } from "@/components/app/shell/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFacility } from "@/lib/facility/facility";
import { useCan, useCanAnywhere } from "@/lib/policy";
import { optionalOneOf, searchPatch } from "@/lib/search";
import { guardRoute } from "@/lib/policy/route-guard";
import { BulkConfirmationsPanel } from "./-components/bulk-confirmations-panel";
import { BulkInvoicesPanel } from "./-components/bulk-invoices-panel";
import { BulkLeaseInvoicesPanel } from "./-components/bulk-lease-invoices-panel";
import { BulkPaymentsPanel } from "./-components/bulk-payments-panel";

const PASSES = ["payments", "confirmations"] as const;

export const Route = createFileRoute("/_authenticated/_administration/bulk-operations")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: (typeof PASSES)[number]; from?: "charges" } => ({
    tab: optionalOneOf(PASSES, search.tab),
    from: optionalOneOf(["charges"], search.from),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "bulk-operations"),
  component: BulkOperations,
});

/** The month-end billing run, done to a whole building at once: raise the month's invoices (from
 * each resident's lease or from their monthly charges), post what came in against them, then clear
 * what settled at the bank. */
function BulkOperations() {
  const { facilityId } = useFacility();
  const { tab, from } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  /* The building on the picker, as the API asks it of each row. Across every building, anywhere the
   * person holds it: their rows are only ever from buildings they belong to, and the organization
   * alone would hide the run from a building's own administrator. */
  const here = useCan("financial:manage", facilityId === "all" ? null : facilityId);
  const anywhere = useCanAnywhere("financial:manage");
  const canWrite = facilityId === "all" ? anywhere : here;

  return (
    <>
      <PageHeader
        title="Bulk operations"
        meta="A building's month-end in three passes: raise, post, clear."
      />

      {/* The three passes are sections of the page; the segmented bar inside Invoices is a
          choice within one, so the two bars must not look alike. */}
      <Tabs
        value={tab ?? "invoices"}
        onValueChange={(next) => patch({ tab: optionalOneOf(PASSES, next) })}
      >
        <TabsList variant="line" fit="wrap" className="justify-start border-b">
          <TabsTrigger value="invoices" className="flex-none px-2.5">
            Invoices
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex-none px-2.5">
            Payments
          </TabsTrigger>
          <TabsTrigger value="confirmations" className="flex-none px-2.5">
            Confirmations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          <Tabs
            value={from ?? "leases"}
            onValueChange={(next) => patch({ from: optionalOneOf(["charges"], next) })}
          >
            <TabsList>
              <TabsTrigger value="leases">From leases</TabsTrigger>
              <TabsTrigger value="charges">From charges</TabsTrigger>
            </TabsList>
            <TabsContent value="leases">
              <BulkLeaseInvoicesPanel canWrite={canWrite} />
            </TabsContent>
            <TabsContent value="charges">
              <BulkInvoicesPanel canWrite={canWrite} />
            </TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="payments">
          <BulkPaymentsPanel canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="confirmations">
          <BulkConfirmationsPanel canWrite={canWrite} />
        </TabsContent>
      </Tabs>
    </>
  );
}
